import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, ErrorHandler, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ApiService, PlatformService } from '@congacommerce/core';
import {
    AttributeRuleService,
    CartItemService,
    CartService,
    ClientConstraintRuleService,
    CommerceModule,
    ProductOptionService,
    ProductService,
    TranslatorLoaderService,
    UserService,
} from '@congacommerce/ecommerce';
import { ApttusModalModule, IconModule, TableModule } from '@congacommerce/elements';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConnectionService } from 'ng-connection-service';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { AppInitializer } from './app-initializer';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ComponentModule } from './common/components/component.module';
import { MaintenanceGuard } from './common/guards/offline.guard';
import { SalesForceLoginGuard } from './common/guards/salesforce-login.guard';
import { TelemetryMetricService } from './common/services/app/telemetry-metric.service';
// Locale data
import { ConfigureGuard } from './common/services/apttus/guard/configure.guard';
import { EglProductService } from './common/services/apttus/tables/product/egl-product.service';
import { AptUserService } from './common/services/apttus/tables/user/apt-user.service';
import { EglApiService } from './common/services/apttus/utility/egl-api.service';
import { EglCartItemService } from './common/services/apttus/utility/egl-cart-item.service';
import { EglCartService } from './common/services/apttus/utility/egl-cart.service';
import { EglClientConstraintRuleService } from './common/services/apttus/utility/egl-client-constraint-rule.service';
import { EglProductOptionService } from './common/services/apttus/utility/egl-product-option.service';
import { D365Service } from './common/services/d365/d365.service';
import { EglPlatformService } from './common/services/shared/egl-platform.service';
import { HttpRequestInterceptorService } from './common/services/shared/http-request-interceptor.service';
import { LoadingService } from './common/services/shared/loading.service';
import { LoggerService } from './common/services/shared/logger.service';
import { MonitoringErrorHandler } from './common/services/shared/monitoring-error.handler';
import { PrivateConfigurationService } from './common/services/shared/private-configuration.service';
import { UtilityService } from './common/services/shared/utility.service';
import { EglAttributeRuleService } from './modules/common/products/services/egl-attribute-rule.service';
import { DataLoaderModule } from './modules/data-loader/data-loader.module';
import { FlowTypeModule } from './modules/flow-type/flow-type.module';
import { BulkOrderOutletComponent } from './routing-outlet/bulk-outlet.component';
import { FullscreenOutletComponent } from './routing-outlet/fullscreen-outlet.component';
import { OrderEntryOutletComponent } from './routing-outlet/order-entry-outlet.component';
import { reducers } from './store/reducers';

@NgModule({
    declarations: [AppComponent, OrderEntryOutletComponent, FullscreenOutletComponent, BulkOrderOutletComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        CommerceModule.forRoot(environment),
        TranslateModule.forRoot({
            loader: { provide: TranslateLoader, useClass: TranslatorLoaderService },
        }),
        ServiceWorkerModule.register('ngsw-worker.js', { enabled: false }),
        TableModule,
        ComponentModule,
        ApttusModalModule,
        ToastrModule.forRoot(),
        IconModule,
        StoreModule.forRoot(reducers, {
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
            },
        }),
        HttpClientModule,
        StoreDevtoolsModule.instrument({
            name: 'EGL.SalesApp',
            maxAge: 25,
            logOnly: environment.production,
        }),
        DataLoaderModule,
        FlowTypeModule,
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: AppInitializer,
            deps: [
                D365Service,
                LoggerService,
                Injector,
                PrivateConfigurationService,
                TelemetryMetricService,
                UtilityService,
            ],
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpRequestInterceptorService,
            multi: true,
        },
        {
            provide: LOCALE_ID,
            deps: [TranslateService],
            useFactory: (translateService) => translateService.currentLang || 'it',
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useValue: 'EUR',
        },
        {
            provide: ErrorHandler,
            useClass: MonitoringErrorHandler,
        },
        {
            provide: ProductOptionService,
            useClass: EglProductOptionService,
        },
        {
            provide: CartService,
            useClass: EglCartService,
        },
        {
            provide: CartItemService,
            useClass: EglCartItemService,
        },
        {
            provide: ClientConstraintRuleService,
            useClass: EglClientConstraintRuleService,
        },
        {
            provide: AttributeRuleService,
            useClass: EglAttributeRuleService,
        },
        {
            provide: UserService,
            useClass: AptUserService,
        },
        {
            provide: ProductService,
            useClass: EglProductService,
        },
        { provide: PlatformService, useClass: EglPlatformService },
        { provide: ApiService, useClass: EglApiService },
        D365Service,
        ConnectionService,
        LoggerService,
        LoadingService,
        ConfigureGuard,
        SalesForceLoginGuard,
        MaintenanceGuard,
        HttpRequestInterceptorService,
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
